import React from 'react';
import {
  AudienceFlowWidgetSchemaKey,
  BehindTheAnswerWidgetKey,
  BriefGenerationWidgetKey,
  ContentTestWidgetKey,
  CopyGenerationWidgetKey,
  FocusGroupWidgetKey,
  EmailFlowWidgetSchemaKey,
  ExternalToolsWidgetKey,
  FourCsWidgetKey,
  HtmlWidgetKey,
  IdeationWidgetKey,
  ImageGenerationWidgetSchemaKey,
  IdeationWidgetKeyV1,
  isHtmlWidgetKey,
  LoremIpsumWidgetSchemaKey,
  SocialBriefWidgetKey,
  SocialOpportunitiesWidgetKey,
  StrategicTerritoriesWidgetKey,
} from '@inference.engine/widgets/web';
import { useQuery } from '@tanstack/react-query';
import { SPECIAL_AGENT_IDS, agentQueryOptions } from '@/entities/agent';
import { i18n } from '@/shared/i18n';
import { normalizeSavedWidgetKey } from './saved-widget-key';
import {
  makeAgentMetadataHelpers,
  type AgentMetadataMap,
} from '@/entities/agent/api/agent-metadata.api';
// Deep-import from entity-internal paths (NOT the `@/entities/agent` barrel)
// to avoid recreating the cycle this file's metadata extraction is meant to
// break: the barrel re-exports `AgentAvatar`, which in turn imports back from
// `@/lib/config/agents.config` (`getAgentBackgroundImageByCategory`). Going
// straight to the model + lib + api files keeps this file decoupled from the
// entity's UI surface.
import { configureAgentMetadata } from '@/entities/agent/lib/agent-metadata-bindings';
import type { AgentMetadata } from '@/entities/agent/model/agent-config.types';
import {
  favoriteOpportunity,
  getOpportunities,
  unfavoriteOpportunity,
} from '@/entities/opportunity';
import { usePersonas } from '@/entities/persona';
import { mapAudienceProfileToSpark, SparkCard } from '@/entities/persona-spark';
import type { WidgetFullModeProps } from '@/entities/widget';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { useArtifactExport } from '@/features/artifacts';
import {
  AudienceFlowThumbnail,
  AudienceFlowWidget as AudienceFlowWidgetCore,
  BehindTheAnswerThumbnail,
  BehindTheAnswerWidget as BehindTheAnswerWidgetCore,
  type AudienceFlowWidgetDeps,
  type AudienceFlowWidgetProps,
  type BehindTheAnswerWidgetProps,
} from '@/features/audience-flow-widget';
import { useStartInterview } from '@/features/audiences';
import { useBriefAgentReview } from '@/features/brief-agent-review';
import { BriefGenerationThumbnail, BriefStartFlow } from '@/features/brief-generation';
import { ContentTestThumbnail } from '@/features/content-test-widget';
import { FocusGroupThumbnail } from '@/features/focus-group-widget';
import {
  getFileDownloadUrl,
  sendOrCreateChat,
  useCurrentAgentId,
  useIsAiBusy,
} from '@/features/chats';
import {
  CopyThumbnail,
  CopyWidget as CopyWidgetCore,
  type CopyGenerationWidgetProps,
  type CopyWidgetExportDeps,
  type CopyWidgetUseArtifactExportOptions,
} from '@/features/copy-widget';
import {
  EmailFlowThumbnail,
  EmailFlowWidget as EmailFlowWidgetCore,
  type EmailFlowWidgetProps,
} from '@/features/email-flow-widget';
import {
  ExternalToolsWidget as ExternalToolsWidgetCore,
  type ExternalToolsWidgetProps,
  type ExternalToolsWidgetSpringboardDeps,
} from '@/features/external-tools-widget';
import { FourCsWidget } from '@/features/four-cs-widget';
import { HtmlWidgetPanel } from '@/features/html-widget';
import {
  IdeationThumbnail,
  IdeationWidget as IdeationWidgetCore,
  type IdeationWidgetBriefDeps,
  type IdeationWidgetProps,
} from '@/features/ideation-widget';
import {
  ImageWidget as ImageWidgetCore,
  type ImageGenerationWidgetProps,
} from '@/features/image-widget';
import { LoremIpsumWidget } from '@/features/lorem-ipsum-widget';
import {
  SocialBriefThumbnail,
  SocialBriefWidget as SocialBriefWidgetCore,
  type SocialBriefWidgetProps,
} from '@/features/social-brief-widget';
import {
  SocialMediaOpportunitiesThumbnail,
  SocialMediaOpportunitiesWidget as SocialMediaOpportunitiesWidgetCore,
  type SocialMediaOpportunitiesApiDeps,
  type SocialMediaOpportunitiesDeps,
  type SocialMediaOpportunitiesWidgetProps,
} from '@/features/social-media-opportunities-widget';
import { SPRINGBOARD_TAB_IDS, useSpringboardTools } from '@/features/springboard';
import {
  StrategicTerritoriesThumbnail,
  StrategicTerritoriesWidget as StrategicTerritoriesWidgetCore,
  type StrategicTerritoriesWidgetBriefDeps,
  type StrategicTerritoriesWidgetProps,
} from '@/features/strategic-territories-widget';
import {
  TemplatesAgentWidget as TemplatesAgentWidgetCore,
  type TemplatesAgentWidgetProps,
} from '@/features/templates-agent-widget';
import { useKnowledgeBaseImages } from '@/features/knowledge-bases';
import { VideoWidget } from '@/features/video-widget';

/**
 * Composition-root agent configuration. Extends the pure `AgentMetadata`
 * (defined in `entities/agent`) with composition-only fields:
 *   - `component`: the React widget component (couples to feature layer,
 *     so cannot live in entities)
 */
interface AgentConfig extends AgentMetadata {
  /**
   * Widget component for this agent.
   * @deprecated Use WIDGET_REGISTRY instead which references the components by widget key.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component?: React.ComponentType<any>;
}

/**
 * TemplatesAgentWidget — config-layer binding.
 *
 * The TemplatesAgent slice exposes a pure functional wrapper that takes the
 * EmailFlow component as an injected prop (see
 * `@/features/templates-agent-widget/index.ts` header for full rationale).
 *
 * The CONFIG layer is the legitimate composition site for cross-feature
 * widget wiring (it already imports BOTH widget slices). Here we bind the
 * actual `EmailFlowWidget` reference into the wrapper, producing the
 * pre-bound component reference consumed by `agentConfiguration`. This
 * keeps cross-widget value-imports at zero in the widget slice itself,
 * with the binding visible at the config-layer composition root.
 */
const TemplatesAgentWidget = (
  props: Omit<TemplatesAgentWidgetProps, 'EmailFlowComponent' | 'useKnowledgeBaseImagesHook'>,
) =>
  React.createElement(TemplatesAgentWidgetCore, {
    ...props,
    EmailFlowComponent: EmailFlowWidget,
    // Bind the kb-image hook here (config layer is the legitimate cross-feature composition site)
    // so the email widget's image picker can list knowledge-base images without the widget slices
    // value-importing the knowledge-bases feature.
    useKnowledgeBaseImagesHook: useKnowledgeBaseImages,
  });

/**
 * CopyWidget — config-layer dependency-injection binding.
 *
 * The CopyWidget slice exposes an `exportDeps?` prop bag
 * (see `model/copy-widget.types.ts`). The CONFIG layer is the legitimate
 * composition site for cross-feature wiring. Here we pre-bind:
 *   - `useArtifactExport` from `@/features/artifacts`
 *   - `getFileDownloadUrl` from `@/features/chats` (folded into the
 *     useArtifactExport options bag so the widget never sees it)
 *
 * The bound wrapper is registered in BOTH `agentConfiguration` (legacy
 * `component` field) and `WIDGET_REGISTRY` (key-based lookup) so every
 * mount path of `CopyWidget` gets the deps. Keeps `features/copy-widget`
 * free of cross-feature value-imports — same posture as `TemplatesAgentWidget`.
 */
const copyWidgetExportDeps: CopyWidgetExportDeps = {
  useArtifactExport: (
    artifactId: string | undefined,
    referenceKey: string | undefined,
    options: CopyWidgetUseArtifactExportOptions,
  ) =>
    useArtifactExport(artifactId, referenceKey, {
      ...options,
      getFileDownloadUrl,
    }),
};

// Render-mode dispatch lives HERE in the registry wrapper, NOT inside each
// widget component. A conditional `if (mode === 'thumbnail') return ...`
// placed before a component's hooks makes every following hook call
// conditional, violating react-hooks/rules-of-hooks. Dispatching at the
// wrapper keeps each inner widget's hook order unconditional (its parameter
// is the full-mode branch only) while still routing thumbnail mode to the
// co-located, prop-only, store-free `*Thumbnail`. `exportDeps` is supplied
// here in full mode, so callers never pass it; the thumbnail branch needs no
// deps, keeping that mount path prop-only.
const CopyWidget = (props: CopyGenerationWidgetProps) =>
  props.mode === 'thumbnail'
    ? React.createElement(CopyThumbnail, { data: props.data })
    : React.createElement<WidgetFullModeProps<CopyGenerationWidgetProps>>(CopyWidgetCore, {
        ...props,
        exportDeps: copyWidgetExportDeps,
      });

// Start Brief flow injected at the config layer, which is
// the legitimate composition site for the `lib → features` edge, so the widget
// slice stays free of cross-feature value-imports.
//
// `BriefStartFlow` (from `@/features/brief-generation`) owns the entire flow —
// picker → intake → progress → open the brief — so this binding stays a thin
// wrapper (same posture as `TemplatesAgentWidget` above). It injects only the
// territory-specific extra: kicking off the agent review. Opening the created
// brief in a new tab is the flow's built-in behavior, so it's not repeated here.
const TerritoryStartBrief = ({ children }: { children: React.ReactNode }) => {
  const { review } = useBriefAgentReview();
  return React.createElement(
    BriefStartFlow,
    { onBriefCreated: (projectId, brief) => void review(brief, projectId) },
    children,
  );
};

const strategicTerritoriesWidgetBriefDeps: StrategicTerritoriesWidgetBriefDeps = {
  StartBrief: TerritoryStartBrief,
};

// Mode dispatch in the wrapper (see the CopyWidget wrapper above for the
// rules-of-hooks rationale): thumbnail mode renders the prop-only
// `StrategicTerritoriesThumbnail`; full mode renders the inner widget with
// `briefDeps` pre-bound here so callers never pass it.
const StrategicTerritoriesWidget = (props: StrategicTerritoriesWidgetProps) =>
  props.mode === 'thumbnail'
    ? React.createElement(StrategicTerritoriesThumbnail, { data: props.data })
    : React.createElement<WidgetFullModeProps<StrategicTerritoriesWidgetProps>>(
        StrategicTerritoriesWidgetCore,
        {
          ...props,
          briefDeps: strategicTerritoriesWidgetBriefDeps,
        },
      );

/**
 * ExternalToolsWidget — config-layer dependency-injection binding.
 * Same factory pattern as the CopyWidget / StrategicTerritoriesWidget
 * bindings above; pre-binds:
 *   - `useSpringboardTools` from `@/features/springboard`
 *   - `SPECIAL_AGENT_IDS.EXTERNAL_TOOLS` (resolved value, NOT the const ref)
 *
 * The widget never imports cross-feature constants — the factory resolves
 * them once here and threads them through `springboardDeps`. Bound wrapper
 * is registered in BOTH `agentConfiguration` and `WIDGET_REGISTRY`.
 * Keeps `features/external-tools-widget` free of cross-feature value-imports.
 *
 * `tabId` is resolved per render rather than pinned here, because an
 * organization can move this agent to another category. The widget looks
 * itself up inside that category's tool list to read its display title, so a
 * stale tab id costs it the title.
 */
const ExternalToolsWidget = (props: Omit<ExternalToolsWidgetProps, 'springboardDeps'>) => {
  const workspaceId = useCurrentWorkspaceId();
  // `GET /agents/:id` applies the override layers, so this tag is the effective one.
  const { data: externalToolsAgent, isPending: isCategoryPending } = useQuery(
    agentQueryOptions(SPECIAL_AGENT_IDS.EXTERNAL_TOOLS, workspaceId),
  );
  const springboardDeps = React.useMemo<ExternalToolsWidgetSpringboardDeps>(
    () => ({
      // Widen the springboard `tabId` parameter from the strict `CategoryId`
      // literal union to a plain `string` here — the widget passes the value
      // it received via `springboardDeps.tabId` (which is also a plain string
      // from the loose mirror). The actual hook still validates `tabId`
      // internally; the wrapper just adapts the type contract.
      //
      // Held back until the category resolves, so the first paint does not fetch, and cache,
      // the tool list of a category this agent may not be in.
      useSpringboardTools: (options) =>
        useSpringboardTools({
          tabId: options.tabId as (typeof SPRINGBOARD_TAB_IDS)[keyof typeof SPRINGBOARD_TAB_IDS],
          enabled: options.enabled && !isCategoryPending,
        }),
      externalToolAgentId: SPECIAL_AGENT_IDS.EXTERNAL_TOOLS,
      tabId: externalToolsAgent?.tag ?? SPRINGBOARD_TAB_IDS.PLAN,
    }),
    [externalToolsAgent?.tag, isCategoryPending],
  );

  return React.createElement(ExternalToolsWidgetCore, {
    ...props,
    springboardDeps,
  });
};

/**
 * AudienceFlowWidget — config-layer dependency-injection binding.
 * Same factory pattern as the CopyWidget / StrategicTerritoriesWidget /
 * ExternalToolsWidget bindings above; pre-binds:
 *   - `usePersonas` from `@/features/persona-sparks` (workspace-scoped
 *     personas list query)
 *   - `useCurrentAgentId` + `sendOrCreateChat` from `@/features/chats`
 *     (selector + chat orchestration helper)
 *   - `audienceFlowPersonaPrefix` — pre-resolved
 *     `SPECIAL_AGENT_IDS.AUDIENCE_FLOW_PERSONA_PREFIX` constant value
 *
 * The widget never imports cross-feature symbols — the factory resolves
 * them once here and threads them through `audienceFlowDeps`. Bound
 * wrapper is registered in BOTH `agentConfiguration` (legacy `component`
 * field, via prefix-keyed `audience-flow-persona-`) and `WIDGET_REGISTRY`
 * (key-based lookup at `AudienceFlowWidgetSchemaKey`) so every mount
 * path of `AudienceFlowWidget` gets the deps. Keeps
 * `features/audience-flow-widget` free of cross-feature value-imports.
 *
 * Note: `usePersonas` from persona-sparks returns a TanStack Query result
 * shape that is structurally assignable to the loose
 * `AudienceFlowUsePersonasReturn` mirror — TS enforces compatibility via
 * the explicit binding below. `sendOrCreateChat` is widened: chats
 * exposes a stricter payload shape; the widget passes `inputs` so the
 * binding accepts both.
 */
const audienceFlowWidgetDeps: AudienceFlowWidgetDeps = {
  // The entity-level `usePersonas` requires `(workspaceId, enabled)`. We
  // adapt to the widget's `(enabled?)` contract by reading `workspaceId`
  // from the workspace store inside the wrapper hook.
  usePersonas: (enabled?: boolean) => {
    const workspaceId = useCurrentWorkspaceId();
    return usePersonas(workspaceId, enabled);
  },
  // Wrap hook references in arrow functions to defer resolution until call
  // time rather than module-init time. This is defensive against the broader
  // chats/widgets module graph: any future code path that causes this module
  // to be loaded before chats finishes initializing would otherwise hit a
  // temporal dead zone for the bare hook reference.
  useCurrentAgentId: () => useCurrentAgentId(),
  useIsChatBusy: () => useIsAiBusy(),
  sendOrCreateChat: (navigate, payload) =>
    sendOrCreateChat(
      navigate as Parameters<typeof sendOrCreateChat>[0],
      payload as Parameters<typeof sendOrCreateChat>[1],
    ),
  audienceFlowPersonaPrefix: SPECIAL_AGENT_IDS.AUDIENCE_FLOW_PERSONA_PREFIX,
};

// Mode dispatch in the wrapper (see the CopyWidget wrapper above for the
// rules-of-hooks rationale): thumbnail mode renders the prop-only
// `AudienceFlowThumbnail`; full mode renders the inner widget with
// `audienceFlowDeps` pre-bound here so callers never pass it.
const AudienceFlowWidget = (props: AudienceFlowWidgetProps) =>
  props.mode === 'thumbnail'
    ? React.createElement(AudienceFlowThumbnail, { data: props.data })
    : React.createElement<WidgetFullModeProps<AudienceFlowWidgetProps>>(AudienceFlowWidgetCore, {
        ...props,
        audienceFlowDeps: audienceFlowWidgetDeps,
      });

/**
 * SocialMediaOpportunitiesWidget — config-layer dependency-injection binding.
 * Same factory pattern as the CopyWidget / StrategicTerritoriesWidget /
 * AudienceFlowWidget bindings above. Pre-binds two prop bags
 * (`opportunitiesDeps` + `socialMediaOpportunitiesDeps`):
 *
 *   1. `opportunitiesDeps` (api): `getOpportunities`,
 *      `favoriteOpportunity`, `unfavoriteOpportunity` from
 *      `@/entities/opportunity`. The widget's `useOpportunities` hook
 *      receives these via the bag.
 *
 *   2. `socialMediaOpportunitiesDeps` (cross-feature helpers):
 *      - `useIsAiBusy`, `sendOrCreateChat` from
 *        `@/features/chats` (also threaded into a 0-arg
 *        `useStartInterview` factory wrapper that pre-binds
 *        `launchAgentWithWidget` + `sendOrCreateChat`).
 *      - `SparkCard`, `mapAudienceProfileToSpark`, `useStartInterview`
 *        from `@/features/persona-sparks` — `SparkCard` is the only
 *        cross-feature React component injection in this widget;
 *        single-consumer + already-factory `useStartInterview` make
 *        injection the cleanest path.
 *
 * The widget never imports cross-feature symbols — the factory resolves
 * them once here and threads them through. Bound wrapper is registered in
 * BOTH `agentConfiguration` (legacy `component` field, multiple agentId
 * entries) and `WIDGET_REGISTRY` (key-based lookup at
 * `SocialOpportunitiesWidgetKey`) so every mount path of
 * `SocialMediaOpportunitiesWidget` gets the deps. Keeps
 * `features/social-media-opportunities-widget` free of cross-feature
 * value-imports.
 *
 * Type widening: `getOpportunities` from opportunities exposes a stricter
 * response shape; the bag mirror declares loose `unknown`-bearing return
 * types, so the structural cast below threads the concrete impl. Same
 * widening pattern for `sendOrCreateChat` (chats exposes a stricter
 * payload type than the widget's loose mirror).
 */
const socialMediaOpportunitiesApiDeps: SocialMediaOpportunitiesApiDeps = {
  getOpportunities: async (params) => {
    const response = await getOpportunities(params as Parameters<typeof getOpportunities>[0]);
    return response.items as unknown as Awaited<
      ReturnType<SocialMediaOpportunitiesApiDeps['getOpportunities']>
    >;
  },
  favoriteOpportunity,
  unfavoriteOpportunity,
};

const socialMediaOpportunitiesDeps: SocialMediaOpportunitiesDeps = {
  // Pre-bind chats helpers into the persona-sparks `useStartInterview`
  // factory so the widget calls a 0-arg variant. Mirrors the audience-flow
  // binding pattern. Cast through `unknown` because the loose
  // `SocialMediaOpportunitiesAudienceProfile` mirror is wider than
  // persona-sparks' strict `AudienceProfile` (extra required fields).
  // `useStartInterview` was lifted to `@/features/audiences` and is now a
  // 0-arg hook that calls `launchAgentWithWidget` / `sendOrCreateChat`
  // internally. The widget binding adapts the return shape only.
  useStartInterview: () =>
    useStartInterview() as unknown as ReturnType<SocialMediaOpportunitiesDeps['useStartInterview']>,
  mapAudienceProfileToSpark: (profile) =>
    mapAudienceProfileToSpark(
      profile as unknown as Parameters<typeof mapAudienceProfileToSpark>[0],
    ),
  // SparkCard's strict prop type (`SparkCardProps` from persona-sparks)
  // narrows `spark` to the concrete `ParsedSpark` type with `persona`,
  // `spark`, `source` required; the widget binding uses a loose
  // `Record<string, unknown>` mirror. Cast through `unknown` at the seam.
  SparkCard: SparkCard as unknown as SocialMediaOpportunitiesDeps['SparkCard'],
  sendOrCreateChat: (navigate, payload) =>
    sendOrCreateChat(
      navigate as Parameters<typeof sendOrCreateChat>[0],
      payload as Parameters<typeof sendOrCreateChat>[1],
    ),
  // Wrapped to defer hook reference resolution to render-time and avoid TDZ
  // ReferenceError from agents.config.ts ↔ features/chats module-init cycle.
  useIsAiBusy: () => useIsAiBusy(),
};

// Mode dispatch in the wrapper (see the CopyWidget wrapper above for the
// rules-of-hooks rationale): thumbnail mode renders the prop-only
// `SocialMediaOpportunitiesThumbnail`; full mode renders the inner widget
// with both dep bags pre-bound here so callers never pass them.
const SocialMediaOpportunitiesWidget = (props: SocialMediaOpportunitiesWidgetProps) =>
  props.mode === 'thumbnail'
    ? React.createElement(SocialMediaOpportunitiesThumbnail, { data: props.data })
    : React.createElement<WidgetFullModeProps<SocialMediaOpportunitiesWidgetProps>>(
        SocialMediaOpportunitiesWidgetCore,
        {
          ...props,
          opportunitiesDeps: socialMediaOpportunitiesApiDeps,
          socialMediaOpportunitiesDeps,
        },
      );

const SocialBriefWidget = (props: SocialBriefWidgetProps) =>
  props.mode === 'thumbnail'
    ? React.createElement(SocialBriefThumbnail, { data: props.data })
    : React.createElement<WidgetFullModeProps<SocialBriefWidgetProps>>(
        SocialBriefWidgetCore,
        props,
      );

// Start Brief flow, injected at the config layer. Thin wrapper over `BriefStartFlow`
// that adds the post-creation agent review (mirrors `TerritoryStartBrief`).
const IdeaStartBrief = ({ children }: { children: React.ReactNode }) => {
  const { review } = useBriefAgentReview();
  return React.createElement(
    BriefStartFlow,
    { onBriefCreated: (projectId, brief) => void review(brief, projectId) },
    children,
  );
};

const ideationWidgetBriefDeps: IdeationWidgetBriefDeps = {
  StartBrief: IdeaStartBrief,
};

// Mode dispatch in the wrapper (see the CopyWidget wrapper above for the
// rules-of-hooks rationale): thumbnail mode renders the prop-only
// `IdeationThumbnail`; full mode renders the inner widget with `briefDeps`
// pre-bound here so callers never pass it.
const IdeationWidget = (props: IdeationWidgetProps) =>
  props.mode === 'thumbnail'
    ? React.createElement(IdeationThumbnail, { data: props.data })
    : React.createElement<WidgetFullModeProps<IdeationWidgetProps>>(IdeationWidgetCore, {
        ...props,
        briefDeps: ideationWidgetBriefDeps,
      });

/**
 * EmailFlowWidget + BehindTheAnswerWidget — mode-dispatch-only wrappers.
 *
 * Neither widget needs config-layer dep injection (both are store-bound via
 * the entities/widget seam, with no pre-bound dep bag). They get a thin
 * wrapper here purely so render-mode dispatch lives at the registry — same
 * rules-of-hooks rationale as the CopyWidget wrapper above: the inner
 * components take only the full-mode props branch (hooks unconditional), and
 * thumbnail mode routes to the co-located prop-only `*Thumbnail` here.
 */
const EmailFlowWidget = (props: EmailFlowWidgetProps) =>
  props.mode === 'thumbnail'
    ? React.createElement(EmailFlowThumbnail, { data: props.data })
    : React.createElement<WidgetFullModeProps<EmailFlowWidgetProps>>(EmailFlowWidgetCore, props);

const BehindTheAnswerWidget = (props: BehindTheAnswerWidgetProps) =>
  props.mode === 'thumbnail'
    ? React.createElement(BehindTheAnswerThumbnail, { data: props.data })
    : React.createElement<WidgetFullModeProps<BehindTheAnswerWidgetProps>>(
        BehindTheAnswerWidgetCore,
        props,
      );

/**
 * ImageWidget — config-layer dependency-injection binding.
 *
 * `features/image-widget/ui/ImageWidget.tsx` previously value-imported
 * `agentConfiguration` from this file to read
 * `agentConfiguration[agentId].allowImageMaskEditing`. That created a
 * module-init cycle (image-widget → agents.config → image-widget) and
 * dragged this whole config module into the widget bundle. Bundlers
 * resolved it via partial exports; safe-ish today, fragile to future
 * refactors.
 *
 * Fix: ImageWidget now accepts `allowImageMaskEditing?: boolean` as a
 * prop (default `true`). The config layer is the legitimate composition
 * site for cross-feature wiring, so we provide per-agent pre-bound
 * wrappers here and register them in `agentConfiguration` /
 * `WIDGET_REGISTRY`. Cycle severed.
 */
const ImageWidgetWithMaskEditing = (props: ImageGenerationWidgetProps) =>
  React.createElement(ImageWidgetCore, {
    ...props,
    allowImageMaskEditing: true,
  });

/**
 * Notes: Consolidated agent configuration - combines UI configs and [widget mappings - DEPRECATED ]
 */
// Getters, not values: this map is built while the module is evaluated, so resolving per
// read is what lets a title follow a language change instead of freezing at import.
const agentConfiguration: Record<string, AgentConfig | undefined> = {
  'strategy-agent-v3': {
    get title() {
      return i18n.t('tools:agentsConfig.strategyAgentV3');
    },
    component: StrategicTerritoriesWidget,
    autoFetchSuggestions: false,
  },
  'social-media-brief': {
    get title() {
      return i18n.t('tools:agentsConfig.socialMediaBrief');
    },
    component: SocialBriefWidget,
    autoFetchSuggestions: false,
  },
  'email-flow-agent': {
    get title() {
      return i18n.t('tools:agentsConfig.emailFlowAgent');
    },
    component: EmailFlowWidget,
    autoFetchSuggestions: true,
  },
  templates: {
    get title() {
      return i18n.t('tools:agentsConfig.templates');
    },
    component: TemplatesAgentWidget,
    autoFetchSuggestions: true,
  },
  'image-generation': {
    get title() {
      return i18n.t('tools:agentsConfig.imageGeneration');
    },
    component: ImageWidgetWithMaskEditing,
    autoFetchSuggestions: false,
  },
  'copy-generation': {
    get title() {
      return i18n.t('tools:agentsConfig.copyGeneration');
    },
    component: CopyWidget,
    autoFetchSuggestions: true,
  },
  'video-generation': {
    get title() {
      return i18n.t('tools:agentsConfig.videoGeneration');
    },
    component: VideoWidget,
    autoFetchSuggestions: true,
  },
  'translation-agent': {
    get title() {
      return i18n.t('tools:agentsConfig.translationAgent');
    },
    autoFetchSuggestions: false,
  },
  'audience-flow-agent': {
    get title() {
      return i18n.t('tools:agentsConfig.audienceFlowAgent');
    },
    autoFetchSuggestions: true,
    initialWidgetState: {
      autoOpen: true,
      data: {
        key: AudienceFlowWidgetSchemaKey,
        data: {},
      },
    },
  },
  ideation: {
    get title() {
      return i18n.t('tools:agentsConfig.ideation');
    },
    autoFetchSuggestions: false,
    component: IdeationWidget,
  },
  'audience-flow-persona-': {
    get title() {
      return i18n.t('tools:agentsConfig.audienceFlowAgent');
    },
    component: AudienceFlowWidget,
    autoFetchSuggestions: true,
    initialWidgetState: {
      autoOpen: true,
      data: {
        key: AudienceFlowWidgetSchemaKey,
        data: {},
      },
    },
    enableDescription: true,
    isPrefixKey: true,
  },
  'lorem-ipsum-agent': {
    get title() {
      return i18n.t('tools:agentsConfig.loremIpsumAgent');
    },
    autoFetchSuggestions: true,
    component: LoremIpsumWidget,
  },
  'external-tools-agent': {
    get title() {
      return i18n.t('tools:agentsConfig.externalToolsAgent');
    },
    autoFetchSuggestions: false,
    widgetOnly: true,
    initialWidgetState: {
      autoOpen: { layoutMode: 'fullscreen' },
      data: {
        key: ExternalToolsWidgetKey,
        data: {},
      },
    },
  },
};

type WidgetKey = string;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const WIDGET_REGISTRY: Partial<Record<WidgetKey, React.ComponentType<any>>> = {
  [LoremIpsumWidgetSchemaKey]: LoremIpsumWidget,
  [AudienceFlowWidgetSchemaKey]: AudienceFlowWidget,
  [BehindTheAnswerWidgetKey]: BehindTheAnswerWidget,
  [CopyGenerationWidgetKey]: CopyWidget,
  [EmailFlowWidgetSchemaKey]: EmailFlowWidget,
  [FourCsWidgetKey]: FourCsWidget,
  // Both V1 and V2 ideation widget keys map to the unified IdeationWidget
  // which handles V1 data transformation internally
  [IdeationWidgetKeyV1]: IdeationWidget,
  [IdeationWidgetKey]: IdeationWidget,
  [SocialBriefWidgetKey]: SocialBriefWidget,
  [SocialOpportunitiesWidgetKey]: SocialMediaOpportunitiesWidget,
  [StrategicTerritoriesWidgetKey]: StrategicTerritoriesWidget,
  [ExternalToolsWidgetKey]: ExternalToolsWidget,
  [HtmlWidgetKey]: HtmlWidgetPanel,
  // A Studio agent's Generate image tool emits this widget too, under the agent's own minted id.
  [ImageGenerationWidgetSchemaKey]: ImageWidgetWithMaskEditing,
};

// Widget keys whose registered component dispatches to a co-located, prop-only
// `*Thumbnail` when mounted in `mode === 'thumbnail'` (see the dispatch wrappers
// above). The Library grid + picker gate live-render on THIS set. Kinds with a
// registered widget but NO thumbnail variant — image/video/template generation,
// FourCs, ExternalTools, LoremIpsum — must NOT mount in thumbnail mode: they'd
// render their full, interactive widget (including buttons) inside the card's
// own clickable button, which is invalid HTML (nested <button>) and throws a
// hydration error. Those kinds fall back to a media thumbnailUrl or the palette
// icon. Keep in sync with the thumbnail dispatch wrappers above.
const WIDGET_THUMBNAIL_KEYS: ReadonlySet<WidgetKey> = new Set([
  CopyGenerationWidgetKey,
  EmailFlowWidgetSchemaKey,
  IdeationWidgetKey,
  IdeationWidgetKeyV1,
  StrategicTerritoriesWidgetKey,
  SocialOpportunitiesWidgetKey,
  BehindTheAnswerWidgetKey,
  SocialBriefWidgetKey,
  AudienceFlowWidgetSchemaKey,
]);

// Widgets that have a thumbnail but NO full-widget registry entry (the brief
// is a generation flow — BriefIntakeModal/Stream/pipeline — not a mountable
// widget). Mapped straight to their prop-only *Thumbnail so the grid/picker
// can live-render them without the widget ever being registered as a full
// widget (which would break the preview modal). Checked BEFORE the
// WIDGET_THUMBNAIL_KEYS/WIDGET_REGISTRY path below.
// Content test + focus group are also thumbnail-only: no full mountable widget,
// just a prop-only `*Thumbnail` rendering from the saved snapshot in `data`.
// Their artifact keys (`content-test:<id>` / `focus-group:<id>`) normalize to
// the canonical widget key via `normalizeSavedWidgetKey` so the resolver finds
// these entries.
const THUMBNAIL_ONLY_BY_KEY: Partial<Record<WidgetKey, React.ComponentType<unknown>>> = {
  [BriefGenerationWidgetKey]: BriefGenerationThumbnail as React.ComponentType<unknown>,
  [ContentTestWidgetKey]: ContentTestThumbnail as React.ComponentType<unknown>,
  [FocusGroupWidgetKey]: FocusGroupThumbnail as React.ComponentType<unknown>,
};

/**
 * Returns the widget component for `widgetKey` ONLY when that widget has a
 * co-located thumbnail variant. Resolves thumbnail-only widgets (no full
 * registry entry — see `THUMBNAIL_ONLY_BY_KEY`) first, then widgets whose
 * registered component dispatches to a thumbnail (see `WIDGET_THUMBNAIL_KEYS`).
 * The Library grid and picker use this to live-render widget thumbnails; it
 * returns `undefined` for every other key so the caller falls back to a media
 * thumbnail or the palette icon. Unlike `getWidgetComponentSync`, it never
 * falls back to the agent's full `component` — that fallback is what leaked
 * full interactive widgets (image/video generation) into the thumbnail slot.
 */
export const getWidgetThumbnailComponentSync = (
  widgetKey: string | null | undefined,
): React.ComponentType<unknown> | undefined => {
  if (!widgetKey) return undefined;
  // Artifacts key off `${prefix}${id}` (saved snapshots AND kinds keyed by their
  // own id like content-test / focus-group); normalize to the canonical widget
  // key FIRST so both the thumbnail-only and registry lookups below match.
  const normalizedKey = normalizeSavedWidgetKey(widgetKey);
  if (THUMBNAIL_ONLY_BY_KEY[normalizedKey]) return THUMBNAIL_ONLY_BY_KEY[normalizedKey];
  if (!WIDGET_THUMBNAIL_KEYS.has(normalizedKey)) return undefined;
  return WIDGET_REGISTRY[normalizedKey];
};

// ============================================================================
// Agent metadata extraction
//
// `agentConfiguration` couples metadata fields (title, suggestions, etc.) with
// the React widget component. We derive a pure `agentMetadata` map (only the
// metadata, no component reference) and bind it into the entity-level helpers
// at module load. Consumers (e.g. `features/chats`) use the bound helpers via
// `getAgentMetadataHelpers()` from `@/entities/agent` — they no longer need to
// import this module, breaking the cyclic dependency that previously formed
// between `lib/config/agents.config.ts` ↔ `features/chats`.
// ============================================================================

const agentMetadata: AgentMetadataMap = Object.fromEntries(
  Object.entries(agentConfiguration).map(([key, config]) => {
    if (!config) return [key, undefined];
    // Copied by property descriptor rather than spread: `title` is a getter, and a
    // spread would call it here and freeze the resolved string at module load.
    // Dropping `component` keeps the copy composition-free, which is what makes it
    // `AgentMetadata` (the deprecation on that field marks the caller-facing API,
    // so it does not apply to this projection).
    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-deprecated
    const { component: _component, ...descriptors } = Object.getOwnPropertyDescriptors(config);
    // Asserted to the projected shape and then assigned to `AgentMetadata`, rather
    // than asserted straight to `AgentMetadata`: `defineProperties` returns `{}`, so
    // a direct assertion would stop checking anything. This way the assignment still
    // fails if `component` ever becomes part of `AgentMetadata`.
    const metadata: AgentMetadata = Object.defineProperties({}, descriptors) as Omit<
      AgentConfig,
      'component'
    >;
    return [key, metadata];
  }),
);

const agentMetadataHelpers = makeAgentMetadataHelpers(agentMetadata);

// Configure the binding so `features/chats` (and any other entity-helper
// consumer) can `getAgentMetadataHelpers()` without importing this file.
configureAgentMetadata(agentMetadataHelpers);

/**
 * Gets the widget component for an agent. Checks the widget registry
 * first, then falls back to the agent's `component` field. Stays in this
 * module because both `WIDGET_REGISTRY` and `agentConfiguration` couple to
 * feature-layer widget components.
 */
export const getWidgetComponentSync = (
  agentName: string,
  widgetKey?: string,
): React.ComponentType<unknown> | undefined => {
  const registryKey = widgetKey && isHtmlWidgetKey(widgetKey) ? HtmlWidgetKey : widgetKey;
  if (registryKey && WIDGET_REGISTRY[registryKey]) {
    return WIDGET_REGISTRY[registryKey];
  }
  // Resolve via prefix-aware lookup, then return the (deprecated) `component`.
  const resolved = (() => {
    const exact = agentConfiguration[agentName];
    if (exact && !exact.isPrefixKey) return exact;
    for (const [key, config] of Object.entries(agentConfiguration)) {
      if (config?.isPrefixKey && agentName.startsWith(key)) return config;
    }
    return undefined;
  })();
  // eslint-disable-next-line @typescript-eslint/no-deprecated
  return resolved?.component;
};
